import { posthog, useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { isTauri } from '@/tauri/utils';
import { useIsVoiceAssistantEnabled } from '@heidi/agents/flags';
import {
  isDictateUserFeedbackIncreasedCadenceEnabled as isDictateUserFeedbackIncreasedCadenceFlagEnabled,
  useIsDictateDeviceDetectedPopupEnabled,
  useIsDictateDeviceSetupEnabled,
  useIsDictateUserFeedbackEnabled as useIsDictateUserFeedbackFlagEnabled,
  useIsDictateUserFeedbackIncreasedCadenceEnabled as useIsDictateUserFeedbackIncreasedCadenceFlagEnabled,
  useIsDictationImportSnippetsEnabled as useIsDictationImportSnippetsFlagEnabled,
} from '@heidi/dictate/flags';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { isTauri as isPlatformTauri } from '@heidi/runtime/platform';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';
import rawPosthog from 'posthog-js';

export const useIsPlatformDictationEnabled = (): boolean => {
  // PostHog-technical: this key is resolved by PostHog on cloud, and the flags
  // door has no PostHog surface, so the raw read stays until the flag migrates.
  const desktopDictationEnabled = useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
  // PostHog-technical, same constraint on the web half of the same gate.
  const webDictationEnabled = useFeatureFlagEnabled(WEB_DICTATION_FLAG) ?? false;
  return isPlatformTauri() ? desktopDictationEnabled : webDictationEnabled;
};

// Non-hook variant for dictation context resolvers used outside React.
export const isDictationContextEnabled = () =>
  IS_PLATFORM_DICTATION_ENABLED() &&
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-enable-dictation-context',
    false
  );

export const useIsDesktopDictationFlagEnabled = () => {
  return useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * SCR-4586: rollout gate for routing dictation into remote-desktop / VDI clients
 * (Citrix Viewer) via synthetic keystrokes. ANDed with the platform dictation
 * rollout per the dictation sub-flag convention. Synced to Rust by
 * `DictationProvider`.
 *
 * Flagsmith/OpenFeature (managed via Terraform in `oscerai/infra`), matching the
 * other `release-scribe-*` sub-flags. Defaults **off** — ships dark for a
 * controlled rollout; enable in Flagsmith once the keystroke path is smoke-tested
 * on a real native Citrix session.
 */
export const useIsDictateRemoteDesktopKeystrokeEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-scribe-dictate-remote-desktop-keystroke',
    false
  );
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Dictaphone (RecMic / PowerMic / SpeechMike) gate: on when EITHER device
 * rollout phase is on — phase 1 (`release-scribe-dictate-device-detected-popup`)
 * or phase 2 (`release-scribe-dictate-device-setup`). Starts the Rust HID
 * reader so a Record press engages dictation, and arms the connect-time
 * awareness card. Desktop / Tauri only — ANDed with isTauri() and the platform
 * dictation parent gate. Synced to Rust by DictationProvider.
 */
export const useIsDictateHardwareMicEnabled = () => {
  const detectedPopupEnabled = useIsDictateDeviceDetectedPopupEnabled();
  const deviceSetupEnabled = useIsDictateDeviceSetupEnabled();
  return (
    isTauri() &&
    (detectedPopupEnabled || deviceSetupEnabled) &&
    IS_PLATFORM_DICTATION_ENABLED()
  );
};

/**
 * SCR-7131: show the native dictation widget on the display containing the
 * cursor (vs always the primary display). Flagsmith/OpenFeature rollout /
 * kill switch — not an experiment, no manual exposure. ANDed with the
 * platform dictation rollout. Synced to Rust by `DictationProvider`.
 * Defaults **off** (primary-monitor placement).
 */
export const useIsDictateWidgetCursorDisplayEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-scribe-dictate-widget-cursor-display',
    false
  );
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * SCR-7481: wider Windows text-field detection for Dictate Anywhere — the
 * whole `Chrome_WidgetWin_<N>` class family and CEF hosts route to the paste
 * path, the UIA probe accepts `TextPattern` / `Edit` / `Document` evidence
 * alongside an active caret, and capture treats a foreground main window as
 * focused (WebView2 steals tao's focus flag). Flagsmith/OpenFeature rollout /
 * kill switch — not an experiment, no manual exposure. ANDed with the platform
 * dictation rollout (`release-desktop-dictation` on desktop). Synced to Rust
 * by `DictationProvider`. Defaults **off** (pre-SCR-7481 detection).
 */
export const useIsDictateBrowserFieldDetectionEnabled = () => {
  const enabled = useBooleanFlagValue('release-dictation-browser-field-detection', false);
  return enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * SCR-6773: desktop Dictate may use a machine-local mic preference independent
 * of the session recording mic. Flagsmith/OpenFeature rollout / kill-switch
 * (not an Amplitude experiment — no manual exposure). Defaults **off**.
 *
 * Desktop/Tauri only and ANDed with the platform dictation parent gate so web
 * never sees the decoupled path.
 */
export const DICTATE_SEPARATE_MICROPHONE_FLAG_KEY =
  'release-scribe-dictate-separate-microphone';

export const useIsDictateSeparateMicrophoneEnabled = () => {
  const enabled = useBooleanFlagValue(DICTATE_SEPARATE_MICROPHONE_FLAG_KEY, false);
  return isTauri() && enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/** Non-hook companion for Rematch effects / imperative paths. */
export const isDictateSeparateMicrophoneEnabled = () => {
  if (!isTauri() || !IS_PLATFORM_DICTATION_ENABLED()) return false;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    DICTATE_SEPARATE_MICROPHONE_FLAG_KEY,
    false
  );
};

export const useIsWebDictationFlagEnabled = () => {
  return useFeatureFlagEnabled(WEB_DICTATION_FLAG) ?? false;
};

export const useIsWebDictationOnboardingFlagEnabled = () => {
  return useBooleanFlagValue('release-web-dictation-onboarding', false);
};

/**
 * Settings Dictaphone row. That row is the entry to the add-device dialog,
 * so the dialog itself is not gated here. Defaults **off**; not synced to Rust.
 */
export const useIsDictateDeviceSetupVisible = () => {
  const enabled = useIsDictateDeviceSetupEnabled();
  return isTauri() && enabled && IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Post-dictation output feedback is subordinate to the current platform's
 * dictation rollout, as well as its own Flagsmith kill switch.
 */
export const useIsDictateUserFeedbackEnabled = (): boolean => {
  const isUserFeedbackEnabled = useIsDictateUserFeedbackFlagEnabled();
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  return isUserFeedbackEnabled && isPlatformDictationEnabled;
};

/** Higher feedback cadence, subordinate to the current platform's Dictate rollout. */
export const useIsDictateUserFeedbackIncreasedCadenceEnabled = (): boolean => {
  const isIncreasedCadenceEnabled = useIsDictateUserFeedbackIncreasedCadenceFlagEnabled();
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  return isIncreasedCadenceEnabled && isPlatformDictationEnabled;
};

/** Non-hook companion for the post-dictation success path. */
export const isDictateUserFeedbackIncreasedCadenceEnabled = (): boolean =>
  isDictateUserFeedbackIncreasedCadenceFlagEnabled() && IS_PLATFORM_DICTATION_ENABLED();

/**
 * The milestone-one snippets importer is subordinate to the platform Dictate
 * rollout as well as its own release flag.
 */
export const useIsDictationImportSnippetsEnabled = (): boolean => {
  const isImportEnabled = useIsDictationImportSnippetsFlagEnabled();
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  return isImportEnabled && isPlatformDictationEnabled;
};

const DICTATION_LANGUAGE_SETTINGS_FLAG_KEY = 'release-dictation-language-settings';

/**
 * Gates the dictation language-settings surface.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped. The flag
 * half is a plain Flagsmith read and would move on its own; what holds it is the
 * composition. It ANDs `useIsPlatformDictationEnabled()`, which resolves through
 * PostHog, and a composition inherits its operand's mechanism —
 * `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting the
 * pair onto the door would change WHICH PROVIDER answers for the platform gate.
 * This is verbatim the case `packages/dictate/src/flags.ts` records as the
 * reason its own siblings stayed.
 *
 * Same classification for `useIsDictationOutputLanguageFlagEnabled` below.
 */
export const useIsDictationLanguageSettingsFlagEnabled = () => {
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  const isLanguageSettingsEnabled = useBooleanFlagValue(
    DICTATION_LANGUAGE_SETTINGS_FLAG_KEY,
    false
  );
  return isPlatformDictationEnabled && isLanguageSettingsEnabled;
};

/**
 * Non-hook companion to `useIsDictationLanguageSettingsFlagEnabled`. Use in
 * helpers/utils where hooks aren't available (e.g. Zustand-store paths that
 * build the dictation transcribe request).
 */
export const isDictationLanguageSettingsEnabled = () => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return (
    IS_PLATFORM_DICTATION_ENABLED() &&
    client.getBooleanValue(DICTATION_LANGUAGE_SETTINGS_FLAG_KEY, false)
  );
};

const DICTATION_OUTPUT_LANGUAGE_FLAG_KEY = 'release-scribe-dictation-output-language';

/**
 * Gates the dictation output-language override (translation). Nested under
 * `release-dictation-language-settings`: the output control only renders when
 * both this flag and the language-settings flag are on.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, same shape and same disqualifier as the
 * language-settings gate above: it ANDs the PostHog-resolved platform-dictation
 * getter, so the composition is not door-readable.
 */
export const useIsDictationOutputLanguageFlagEnabled = () => {
  const isPlatformDictationEnabled = useIsPlatformDictationEnabled();
  const isOutputLanguageEnabled = useBooleanFlagValue(
    DICTATION_OUTPUT_LANGUAGE_FLAG_KEY,
    false
  );
  return isPlatformDictationEnabled && isOutputLanguageEnabled;
};

/**
 * Non-hook companion to `useIsDictationOutputLanguageFlagEnabled`. Use in
 * helpers/store code that builds the transcribe `user_context` outside React
 * (e.g. `getDefaultDictationOutputLanguages`).
 */
export const isDictationOutputLanguageEnabled = () => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  return (
    IS_PLATFORM_DICTATION_ENABLED() &&
    client.getBooleanValue(DICTATION_OUTPUT_LANGUAGE_FLAG_KEY, false)
  );
};

/**
 * Customizable dictation hotkey settings (desktop only). Requires Tauri + the
 * desktop-dictation parent flag. The former `release-dictation-hotkey-settings`
 * OpenFeature gate shipped to 100% and was removed.
 */
export const useIsDictationHotkeySettings = () => {
  const desktopDictationEnabled = useFeatureFlagEnabled(DESKTOP_DICTATION_FLAG) ?? false;
  return isTauri() && desktopDictationEnabled;
};

/**
 * Non-hook companion to `useIsDictationHotkeySettings`. Use in helpers/utils
 * where hooks aren't available (e.g. `getDictationHotkey`).
 */
export const isDictationHotkeySettingsEnabled = () => {
  if (!isTauri()) return false;
  return rawPosthog.isFeatureEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * Shipped 100% (Flagsmith). Semantic alias: per-user dictation log opt-out UI
 * and `log_dictations_enabled` on transcribe requests when platform dictation
 * is on. Non-hook for Zustand stores and Tauri config.
 */
export const isDictationLogOptOutEnabled = () => IS_PLATFORM_DICTATION_ENABLED();

/**
 * Shipped 100% (Flagsmith). Alias for platform dictation rollout in settings UI.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, and it
 * fails the move test twice over. It is a bare ALIAS of the PostHog-resolved
 * platform-dictation getter, so it reads no flag of its own and there is no
 * key/default pair for a descriptor to hold; and by noun it is dictation's
 * rather than workspace's, so even once the platform gate is door-readable it
 * belongs to that domain's lane.
 */
export const useIsDictationLogOptOutEnabled = useIsPlatformDictationEnabled;

/**
 * Gates the Talk-to-Heidi dictation mode surface (Start dictation control +
 * capture bar). PostHog-backed against the same
 * `release-ml-scribe-voice-dictation-mode` flag the backend tools/frames gate
 * on, ANDed with the umbrella voice-assistant flag so a dictation bug can be
 * killed without disabling the whole assistant — and so the button never
 * appears before the backend understands `start_dictation`. Defaults OFF.
 */
export const useIsVoiceDictationModeEnabled = () => {
  const voiceOn = useIsVoiceAssistantEnabled();
  const dictationOn =
    useFeatureFlagEnabled('release-ml-scribe-voice-dictation-mode') ?? false;
  return voiceOn && dictationOn;
};

export const IS_DICTATION_MOBILE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-dictation-mobile');
};

// This should not be cleaned up because desktop does not support tab-dictation yet
export const IS_TAB_DICTATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-tab-dictation');
};

/**
 * Gates the Dictate history page and its sidebar entry. Requires the
 * platform's dictation rollout (`IS_PLATFORM_DICTATION_ENABLED`).
 * `release-scribe-enable-dictation-log` shipped 100% — removed.
 */
export const IS_DICTATION_LOGS_VISIBLE = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/** Shipped 100% — Flagsmith `release-scribe-enable-dictation-log`. */
export const IS_DICTATION_LOGS_ENABLED = () => {
  return true;
};

/**
 * SCR-3993: gates optional stats on the Dictate history page. ANDs the
 * platform's dictation rollout (`IS_PLATFORM_DICTATION_ENABLED`) so stats
 * never resolve true outside the platform's dictation rollout (SCR-5860).
 * `release-scribe-enable-dictation-log-stats` shipped 100% — removed.
 */
export const IS_DICTATION_LOGS_STATS_ENABLED = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Gates the stats section on the Dictate history page. The platform gate now
 * lives in `IS_DICTATION_LOGS_STATS_ENABLED`, so this is a semantic alias.
 */
export const IS_DICTATION_LOGS_STATS_VISIBLE = () => {
  return IS_DICTATION_LOGS_STATS_ENABLED();
};

/** Shipped 100% (Flagsmith). v2 transcription backend when platform dictation is on. */
export const IS_DICTATION_LOGS_BACKEND_ENABLED = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Gates the Tone tab — desktop-only + platform dictation rollout.
 * Former Flagsmith gate shipped 100%; keep `isTauri` + platform AND.
 */
export const IS_DICTATION_TONE_SETTINGS_VISIBLE = () => {
  return isTauri() && IS_PLATFORM_DICTATION_ENABLED();
};

/** PostHog feature flag key: desktop (Tauri) dictation rollout. */
export const DESKTOP_DICTATION_FLAG = 'release-desktop-dictation';

/** PostHog feature flag key: web dictation rollout (legacy key name: `universal-dictation`). */
export const WEB_DICTATION_FLAG = 'universal-dictation';

export const IS_WEB_DICTATION_ENABLED = () => {
  return posthog.isFeatureEnabled(WEB_DICTATION_FLAG) ?? false;
};

/**
 * Desktop Dictation: Enables dictation bubble on the Tauri desktop app.
 * Uses Tauri's native microphone permission handling for proper macOS integration.
 *
 * Raw desktop dictation rollout/platform flag.
 */
export const IS_DESKTOP_DICTATION_ENABLED = () => {
  if (!isTauri()) return false;
  return posthog.isFeatureEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * The current platform's dictation rollout gate (desktop: `release-desktop-dictation`,
 * web: `universal-dictation`). Every dictation sub-feature flag should AND with this
 * so the feature never appears unless the user is in the platform's dictation rollout.
 * Non-hook twin of `useIsDictationSettingsEnabled`.
 */
export const IS_PLATFORM_DICTATION_ENABLED = () => {
  return isTauri() ? IS_DESKTOP_DICTATION_ENABLED() : IS_WEB_DICTATION_ENABLED();
};

/**
 * Persists the dictation playback popover's audio position so it resumes from
 * where the user left off when they close and reopen it. Behind a flag so we
 * can disable it remotely if the position-restore behaviour misbehaves.
 */
export const IS_DICTATION_PLAYBACK_RESUME_ENABLED = () => {
  return posthog.isFeatureEnabled('release-dictation-playback-resume') ?? false;
};
